import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';

const Profile = () => {
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    medicalHistory: '',
    menstrualHistory: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No token found. Please log in again.');

        const response = await axios.get('https://backendumbracare.onrender.com/api/users/me', {
          headers: { 'x-auth-token': token },
        });
        setUser(response.data);
        setFormData({
          name: response.data.name || '',
          email: response.data.email || '',
          medicalHistory: response.data.medicalHistory || '',
          menstrualHistory: response.data.menstrualHistory || '',
        });
      } catch (err) {
        console.error('Error fetching user profile:', err.response?.data || err.message);
        setError(`Error fetching profile: ${err.response?.data?.msg || err.message}`);
      }
    };

    fetchUserProfile();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found. Please log in again.');
      }
      const response = await axios.put(
        'https://backendumbracare.onrender.com/api/users/me',
        formData,
        {
          headers: {
            'x-auth-token': token,
          },
        }
      );
      setUser(response.data);
      setIsEditing(false);
      setSuccess('Profile updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating profile:', err.response?.data || err.message);
      setError(`Error updating profile: ${err.response?.data?.msg || err.message}`);
    }
  };
  

  const handleUseForChange = async (e) => {
    const { useFor } = e.target.value;
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found. Please log in again.');
      }
      const response = await axios.put(
        'https://backendumbracare.onrender.com/api/users/useFor',
        { useFor },
        { headers: { 'x-auth-token': token } }
      );
      setUser(response.data);
      setSuccess('Use for updated successfully!');
      window.location.href = '/dashboard';
      setTimeout(() => setSuccess(''), 3000);
      localStorage.setItem('useFor', useFor);
    } catch (err) {
      console.error('Error updating use for:', err.response?.data || err.message);
      setError(`Error updating use for: ${err.response?.data?.msg || err.message}`);
    }
  };

  if (!user && error) return <div style={styles.container}>{error}</div>;
  if (!user) return <div style={styles.container}>Loading...</div>;

  return (
    <motion.div 
      style={styles.container}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div 
        style={styles.profileContainer}
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        <motion.h2 
          style={styles.heading}
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          User Profile
        </motion.h2>
        
        <motion.div 
          style={styles.useForContainer}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <p style={styles.useForText}>You are using for: {localStorage.getItem('useFor')}</p>
          <select 
            value={localStorage.getItem('useFor') || ''}
            onChange={(e) => {
              const useFor = e.target.value;
              handleUseForChange({ target: { value: { useFor } } });
            }}
            style={styles.select}
          >
            <option value="pregnancy">Pregnancy</option>
            <option value="period">Period</option>
          </select>
        </motion.div>

        {error && (
          <motion.p 
            style={styles.error}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {error}
          </motion.p>
        )}
        
        {success && (
          <motion.p 
            style={styles.success}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {success}
          </motion.p>
        )}

        {!isEditing ? (
          <motion.div 
            style={styles.viewMode}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            <div style={styles.formGroup}>
              <label style={styles.label}>Name:</label>
              <input
                type="text"
                value={user.name || 'Not provided'}
                readOnly
                style={styles.readOnlyInput}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Email:</label>
              <input
                type="email"
                value={user.email || 'Not provided'}
                readOnly
                style={styles.readOnlyInput}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Medical History:</label>
              <textarea
                value={user.medicalHistory || 'e.g., Allergies, past surgeries, chronic conditions...'}
                readOnly
                style={{
                  ...styles.readOnlyTextarea,
                  color: user.medicalHistory ? '#000000' : '#999',
                }}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Menstrual History:</label>
              <textarea
                value={user.menstrualHistory || 'e.g., Average cycle length, last period date, any irregularities...'}
                readOnly
                style={{
                  ...styles.readOnlyTextarea,
                  color: user.menstrualHistory ? '#000000' : '#999',
                }}
              />
            </div>
            <motion.button 
              onClick={() => setIsEditing(true)} 
              style={styles.button}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Update Profile
            </motion.button>
          </motion.div>
        ) : (
          <motion.form 
            onSubmit={handleSubmit} 
            style={styles.form}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            <div style={styles.formGroup}>
              <label style={styles.label}>Name:</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                style={styles.input}
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Email:</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                style={styles.input}
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Medical History:</label>
              <textarea
                name="medicalHistory"
                value={formData.medicalHistory}
                onChange={handleChange}
                style={styles.textarea}
                placeholder="e.g., Any allergies, chronic conditions, or past surgeries"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Menstrual History:</label>
              <textarea
                name="menstrualHistory"
                value={formData.menstrualHistory}
                onChange={handleChange}
                style={styles.textarea}
                placeholder="e.g., Average cycle length, last period date, any irregularities"
              />
            </div>
            <motion.div style={styles.buttonGroup}>
              <motion.button 
                type="submit" 
                style={styles.button}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Save Changes
              </motion.button>
              <motion.button
                type="button"
                onClick={() => setIsEditing(false)}
                style={styles.cancelButton}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Cancel
              </motion.button>
            </motion.div>
          </motion.form>
        )}
      </motion.div>
    </motion.div>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    fontFamily: "'Poppins', sans-serif",
    boxSizing: 'border-box',
    paddingTop: '80px',
    backgroundImage: 'url(/background.jpg)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    position: 'relative',
    zIndex: 1,
    '::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(253, 232, 233, 0.7)',
      zIndex: -1,
    }
  },

  profileContainer: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    padding: '40px',
    borderRadius: '20px',
    boxShadow: '0 8px 32px rgba(177, 88, 112, 0.1)',
    textAlign: 'center',
    border: '1px solid rgba(177, 88, 112, 0.2)',
    width: '100%',
    maxWidth: '600px',
    position: 'relative',
    zIndex: 2,
  },
  heading: {
    fontSize: '32px',
    color: '#B85170',
    marginBottom: '30px',
    fontWeight: '600',
    textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
  },
  useForContainer: {
    display: 'flex',
    gap: '15px',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '30px',
    padding: '15px',
    backgroundColor: 'rgba(177, 88, 112, 0.05)',
    borderRadius: '10px',
  },
  useForText: {
    color: '#B85170',
    fontWeight: '500',
    fontSize: '16px',
  },
  select: {
    padding: '8px 15px',
    fontSize: '14px',
    borderRadius: '8px',
    border: '1px solid rgba(177, 88, 112, 0.3)',
    backgroundColor: 'white',
    color: '#B85170',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  input: {
    padding: '12px 15px',
    fontSize: '16px',
    borderRadius: '10px',
    border: '1px solid rgba(177, 88, 112, 0.3)',
    width: '100%',
    boxSizing: 'border-box',
    color: '#333',
    transition: 'all 0.3s ease',
    '&:focus': {
      borderColor: '#B85170',
      boxShadow: '0 0 0 2px rgba(177, 88, 112, 0.2)',
    },
  },
  textarea: {
    padding: '12px 15px',
    fontSize: '16px',
    borderRadius: '10px',
    border: '1px solid rgba(177, 88, 112, 0.3)',
    width: '100%',
    boxSizing: 'border-box',
    minHeight: '120px',
    color: '#333',
    transition: 'all 0.3s ease',
    '&:focus': {
      borderColor: '#B85170',
      boxShadow: '0 0 0 2px rgba(177, 88, 112, 0.2)',
    },
  },
  button: {
    backgroundColor: '#B15870',
    color: '#fff',
    padding: '12px 25px',
    fontSize: '16px',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    width: '100%',
    boxShadow: '0 4px 6px rgba(177, 88, 112, 0.2)',
    '&:hover': {
      backgroundColor: '#9d4a60',
      transform: 'translateY(-2px)',
    },
  },
  cancelButton: {
    backgroundColor: '#f0f0f0',
    color: '#666',
    padding: '12px 25px',
    fontSize: '16px',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    '&:hover': {
      backgroundColor: '#e0e0e0',
      transform: 'translateY(-2px)',
    },
  },
  success: {
    fontSize: '16px',
    backgroundColor: 'rgba(61, 52, 139, 0.1)',
    color: '#3D348B',
    marginBottom: '20px',
    padding: '12px',
    borderRadius: '10px',
    border: '1px solid rgba(61, 52, 139, 0.2)',
  },
  error: {
    fontSize: '16px',
    backgroundColor: 'rgba(255, 68, 68, 0.1)',
    color: '#FF4444',
    marginBottom: '20px',
    padding: '12px',
    borderRadius: '10px',
    border: '1px solid rgba(255, 68, 68, 0.2)',
  },
  viewMode: {
    textAlign: 'left',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '5px',
  },
  label: {
    fontSize: '16px',
    color: '#B85170',
    fontWeight: '500',
  },
  readOnlyInput: {
    padding: '10px',
    fontSize: '16px',
    borderRadius: '5px',
    border: '1px solid #ccc',
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: '#f9f9f9',
    color: '#000000',
  },
  readOnlyTextarea: {
    padding: '10px',
    fontSize: '16px',
    borderRadius: '5px',
    border: '1px solid #ccc',
    width: '100%',
    boxSizing: 'border-box',
    minHeight: '100px',
    backgroundColor: '#f9f9f9',
  },
  buttonGroup: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'center',
  },
};

export default Profile;